import { Button } from "@talladega/components";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { ReactElement, useEffect } from "react";
import PublicLayout from "@layouts/public/PublicLayout";
import { GetStaticPropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";
import { gtmPushData } from "@talladega/helpers";
import {
  ArrowRightIcon,
  EmailIcon,
  ShieldPositiveIcon,
  WarningIcon,
} from "@talladega/icons";
import BackgroundImage404 from "@public/images/hero-background.png";

const NotFound = () => {
  const { t } = useTranslation(["common", "purchase"]);

  useEffect(() => {
    gtmPushData({
      event: "404 Page Not Found",
      path: window.location.href,
    });
    hotjar.event(HotjarEvent.PageNotFound);
  }, []);

  return (
    <>
      <div
        className="relative flex items-center justify-center h-full py-10"
        style={{
          backgroundImage: `url(${BackgroundImage404.src})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 mx-auto max-w-page h-[28rem] flex items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-8 px-4 text-center md:px-20 xl:px-0">
            <div className="flex flex-col items-center">
              <div className="flex items-center justify-center rounded-full bg-gray-500 w-16 h-16">
                <WarningIcon className="h-10 w-10 text-white" />
              </div>
            </div>

            <h1 className="pb-4 md:pt-10">
              <div className="inline text-white">
                {t("purchase:this_page_is_out_of_warranty")}
              </div>
            </h1>
            <p className="pb-4 text-lg font-semibold text-white">
              {t("common:looks_like_the_page_took_wrong_turn")}
            </p>
            <Link href="/">
              <Button buttonStyle="dark-secondary">
                {t("common:go_home")}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="py-12 max-w-3xl mx-auto grid grid-cols-1 gap-y-6 xl:gap-y-12 gap-x-8 xl:gap-x-12 grid-flow-row text-center lg:grid-cols-2 sm:max-w-5xl">
        <div className="group flex flex-col flex-1 gap-4 items-start p-6 md:p-10 md:pr-64 rounded-lg w-full h-auto bg-gray-50 transition shadow-lg">
          <ShieldPositiveIcon className="h-6 w-6 mb-8" />
          <p className="text-2xl font-semibold text-gray-900 text-left">
            {t("purchase:warranty_plans")}
          </p>
          <p className="text-base text-gray-600 text-left">
            {t("purchase:make_sure_youre_always_covered")}
          </p>
          <Link href="/">
            <Button
              fill="link"
              buttonStyle="borderless"
              spacing="no-padding"
              rightIcon={<ArrowRightIcon />}
              className="text-primary-700 ml-[-8px]"
            >
              {t("purchase:get_a_free_quote")}
            </Button>
          </Link>
        </div>

        <div className="group flex flex-col flex-1 gap-4 items-start p-6 md:p-10 md:pr-64 rounded-lg w-full h-auto bg-gray-50 transition shadow-lg">
          <EmailIcon className="h-6 w-6 mb-8" />
          <p className="text-2xl font-semibold text-gray-900 text-left">
            {t("common:contact_us")}
          </p>
          <p className="text-base text-gray-600 text-left">
            {t("purchase:we_will_help_you_find")}
          </p>
          <Link href="/contact">
            <Button
              fill="link"
              buttonStyle="borderless"
              spacing="no-padding"
              rightIcon={<ArrowRightIcon />}
              className="text-primary-700 ml-[-8px]"
            >
              {t("common:contact_our_team")}
            </Button>
          </Link>
        </div>
      </div>
    </>
  );
};

NotFound.pageName = "NotFound";
NotFound.getLayout = function getLayout(page: ReactElement) {
  return <PublicLayout>{page}</PublicLayout>;
};

export async function getStaticProps(context: GetStaticPropsContext) {
  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? "en", [
        "common",
        "common-components",
      ])),
    },
  };
}

export default NotFound;
